/* eslint-disable no-useless-return */
import React, { useEffect, useState, useCallback } from 'react';
import TextInput, { TextInputProps } from '../TextInput';
import {
  dateToTimeString,
  isTimeEqual,
  ParsedDate,
  parseTimeString,
} from './helpers';
import { DAY_START_HOURS, HALF_DAY_HOURS, TimeInputFormat } from './constants';

export interface TimeInputProps extends Omit<TextInputProps, 'value'> {
  value?: Date;
  onChangeTime: (
    dateValue: Date | null,
    inputValue: string | null,
    parsedFormat: TimeInputFormat | null
  ) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

const TimeInput = React.forwardRef<HTMLInputElement, TimeInputProps>(
  (props, ref) => {
    const { onChange, onBlur, value, onChangeTime, ...otherProps } = props;

    const [currentInputValue, setCurrentInputValue] = useState<string>(
      dateToTimeString(value)
    );
    const [
      currentParsedDate,
      setCurrentParsedDate,
    ] = useState<ParsedDate | null>(parseTimeString(currentInputValue));

    const [isInputTrigger, setIsInputTrigger] = useState(false);

    useEffect(() => {
      // component value changed
      const timeString = dateToTimeString(value);
      setCurrentInputValue(timeString);
      setCurrentParsedDate(parseTimeString(timeString));
    }, [value]);

    const onCurrentInputValueChanged = useCallback(
      (force = false) => {
        if (
          !force &&
          !isInputTrigger &&
          !isTimeEqual(value, currentParsedDate && currentParsedDate.value)
        ) {
          return;
        }
        if (isInputTrigger) {
          setIsInputTrigger(false);
        }
        if (!force && currentParsedDate && currentParsedDate.format.invalid) {
          return;
        }

        // transfer time to user date value
        const changedDate =
          currentParsedDate && !currentParsedDate.format.invalid
            ? (value && new Date(value)) || new Date()
            : null;
        if (changedDate && currentParsedDate) {
          changedDate.setHours(currentParsedDate.value.getHours());
          changedDate.setMinutes(currentParsedDate.value.getMinutes());

          // convert 1-7am to pm on blur
          if (
            force &&
            currentParsedDate &&
            currentParsedDate.format.is24 &&
            changedDate.getHours() < DAY_START_HOURS
          ) {
            changedDate.setHours(changedDate.getHours() + HALF_DAY_HOURS);
          }
        }

        const userTimeStringValue = dateToTimeString(value);

        if (isTimeEqual(value, changedDate)) {
          if (force && currentInputValue !== userTimeStringValue) {
            setCurrentInputValue(userTimeStringValue);
          }
          return;
        }

        // is value can`t be 12h format
        const isStrict24 =
          currentParsedDate &&
          currentParsedDate.format.is24 &&
          currentParsedDate.value.getHours() > HALF_DAY_HOURS;

        // is typed format can be rated as fully correct
        const formatExact =
          !currentParsedDate ||
          (currentParsedDate.format.exact &&
            (!currentParsedDate.format.is24 || isStrict24));

        if (!formatExact && !force) {
          return;
        }

        onChangeTime(
          changedDate,
          currentInputValue,
          (currentParsedDate || { format: null }).format
        );
      },
      [
        currentInputValue,
        currentParsedDate,
        value,
        onChangeTime,
        isInputTrigger,
      ]
    );

    useEffect(onCurrentInputValueChanged, [onCurrentInputValueChanged]);

    const onInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange && onChange(event); // trigger native onChange, to allow use defaultPrevented
        if (
          !event.defaultPrevented &&
          event.target.value !== currentInputValue
        ) {
          const parsedDate = parseTimeString(event.target.value);
          if (!event.target.value || parsedDate) {
            setCurrentInputValue(event.target.value);
            setCurrentParsedDate(parsedDate);
            setIsInputTrigger(true);
          }
        }
      },
      [currentInputValue, onChange]
    );

    const onInputBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        onBlur && onBlur(event);
        if (!event.defaultPrevented) {
          onCurrentInputValueChanged(true);
        }
      },
      [onBlur, onCurrentInputValueChanged]
    );

    return (
      <TextInput
        ref={ref}
        {...otherProps}
        value={currentInputValue}
        onChange={onInputChange}
        onBlur={onInputBlur}
      />
    );
  }
);

export default TimeInput;
